import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    linkedSignal,
    signal,
    untracked,
} from "@angular/core";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25States } from "../../../services/state/state.map";
import { StateService } from "../../../services/state.service";
import { S25RouterLinkDirective } from "../../s25-nav-bar/s25.router.link.directive";
import { StudentEngagementOrg } from "../StudentEngagementOrg";
import SeOrgTile = StudentEngagementOrg.SeOrgTile;
import { SeObjectSearchStore } from "../search/object-search/se.object.search.store";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { S25EditablePropertyComponent } from "../../s25-editable-property/s25.editable.property.component";
import { Item } from "../../../pojo/Item";
import { UserStore } from "../../../stores/user.store";
import { S25Datefilter } from "../../s25-dateformat/s25.datefilter.service";
import { S25CheckboxModule } from "../../s25-checkbox/s25.checkbox.module";
import { S25Array } from "../../../util/core/s25.array";
import { S25Promise } from "../../../util/core/s25.promise";
import { S25Error } from "../../../util/core/s25.error";
import { s25Resource } from "../../../util/s25.resource";
import { StudentEngagementService } from "../student.engagement.service";
import { transformLogoDisplayUrl } from "../logo-url.coordinator";
import { S25ContactProfileImageComponent } from "../contact/contact.profile.image.component";
import { S25IconButtonComponent } from "../../../standalone/s25.icon.button.component";
import { S25Const } from "../../../util/s25-const";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { OrganizationService } from "../../../services/organization.service";
import { OrganizationProfileApi } from "../../../services/organization/organization.profile.api";
import { S25PipesModule } from "../../../pipes/s25.pipes.module";
import { S25FavoriteButtonComponent } from "../../../standalone/s25-favorite-button/s25.favorite.button.component";
import { ItemService } from "../../../util/items/item.service";

@TypeManagerDecorator("se-organization-tile")
@Component({
    selector: "se-organization-tile",
    host: { "[class.is-admin]": "isAdmin()" },
    template: `
        <article class="org-tile-wrapper" [attr.aria-labelledby]="'org-name-' + org().itemId">
            @if (isAdmin()) {
                <s25-ng-checkbox
                    class="org-card__check"
                    [value]="selected()"
                    [ariaLabel]="'Select ' + displayName() + ' organization'"
                    (valueChange)="setSelected($event)"
                />
                <a
                    class="org-tile-link"
                    s25-ng-router-link
                    [stateName]="routingStateName"
                    [stateParams]="stateParams()"
                    [attr.aria-label]="'View ' + displayName() + ' organization details'"
                >
                    <div class="org-image-container">
                        @if (logoUrl()) {
                            <img
                                [src]="logoUrl()"
                                [alt]="displayName() + ' logo'"
                                class="org-image"
                                (error)="logoUrl.set(undefined)"
                            />
                        } @else {
                            <div class="org-image-placeholder" aria-hidden="true">
                                <s25-ng-icon [type]="'organization'" [size]="adminIconSize()" [ariaLabel]="false" />
                            </div>
                        }
                    </div>
                </a>
                <div class="org-details">
                    <div class="org-details__header">
                        <a
                            class="org-name-link"
                            s25-ng-router-link
                            [stateName]="routingStateName"
                            [stateParams]="stateParams()"
                        >
                            <h3 [id]="'org-name-' + org().itemId" class="org-name">{{ displayName() }}</h3>
                        </a>
                        <div
                            class="status-wrapper"
                            [class.has-status]="ratingInput()?.itemId"
                            [attr.data-standing]="ratingStanding()"
                            [attr.aria-label]="statusAccessibleLabel()"
                        >
                            @if (ratingInput()?.itemId) {
                                <s25-ng-icon
                                    class="standing-icon"
                                    [class.standing-icon--allow]="ratingStanding() === 'allow'"
                                    [class.standing-icon--warn]="ratingStanding() === 'warn'"
                                    [class.standing-icon--deny]="ratingStanding() === 'deny'"
                                    [type]="standingIcon()"
                                    [size]="14"
                                    [ariaLabel]="false"
                                />
                            }
                            <s25-ng-dropdown-search-criteria
                                class="status-dropdown"
                                [class.has-status]="ratingInput()?.itemId"
                                [attr.data-standing]="ratingStanding()"
                                [type]="'organizationRatings'"
                                [(value)]="ratingInput"
                                [placeholder]="'Add Status'"
                                [ariaLabel]="'Organization status for ' + displayName()"
                                (valueChange)="updateRating($event!)"
                            />
                        </div>
                    </div>
                    @if (errorMsg()) {
                        <p class="ngRed" role="alert">{{ errorMsg() }}</p>
                    }
                    @if (org().type) {
                        <p class="org-card__type">{{ org().type }}</p>
                    }
                    @if (org()?.mission) {
                        <div class="org-mission" [innerHTML]="org().mission! | safeHTML"></div>
                    }
                </div>
                <div class="org-card__body">
                    <dl class="org-card__stats" aria-label="Organization statistics">
                        <div class="stat">
                            <dt class="stat__label">Members</dt>
                            <dd class="stat__value">{{ numMembers() }}</dd>
                        </div>
                        <div class="stat stat--accent">
                            <dt class="stat__label">Officers</dt>
                            <dd class="stat__value">{{ numOfficers() }}</dd>
                        </div>
                        <div class="stat">
                            <dt class="stat__label">Events</dt>
                            <dd class="stat__value">{{ org().eventCount }}</dd>
                        </div>
                    </dl>
                    <s25-ng-editable-property
                        class="org-card__categories"
                        [itemId]="org().itemId"
                        [itemTypeId]="OrgTypeId"
                        [canEdit]="isAdmin()"
                        [properties]="org().categories!"
                        [propType]="'organizationCategories'"
                        [propPayloadType]="'categories'"
                    ></s25-ng-editable-property>
                    @if (modDt()) {
                        <p class="org-card__audit">Updated {{ modDt() }}</p>
                    }
                </div>
                <div class="org-card__rail">
                    @if (primaryContact(); as contact) {
                        <div class="org-card__contact">
                            <s25-ng-contact-profile-image [contactId]="contact.itemId" [name]="contact.itemName" />
                            <div class="contact-info">
                                <span class="contact-label" [id]="'contact-label-' + org().itemId">Contact</span>
                                <span class="contact-name" [attr.aria-labelledby]="'contact-label-' + org().itemId">
                                    {{ contact.itemName }}
                                </span>
                            </div>
                            @if (contact.itemEmail) {
                                <s25-ng-icon-button
                                    class="contact-email-btn"
                                    [type]="'mail'"
                                    [size]="18"
                                    [ariaLabel]="'Email ' + contact.itemName + ', primary contact for ' + displayName()"
                                    (clicked)="emailPrimaryContact($event)"
                                />
                            }
                        </div>
                    }
                    <button
                        type="button"
                        class="btn btn--sm"
                        [attr.aria-label]="'Email ' + displayName() + ' organization'"
                        (click)="openEmailSidebar()"
                    >
                        Email
                    </button>
                </div>
            } @else {
                <a
                    class="org-tile-link"
                    s25-ng-router-link
                    [stateName]="routingStateName"
                    [stateParams]="stateParams()"
                    [attr.aria-label]="'View ' + displayName() + ' organization details'"
                >
                    <div class="org-image-container">
                        @if (logoUrl()) {
                            <img
                                [src]="logoUrl()"
                                [alt]="displayName() + ' logo'"
                                class="org-image"
                                (error)="logoUrl.set(undefined)"
                            />
                        } @else {
                            <div class="org-image-placeholder" aria-hidden="true">
                                <s25-ng-icon [type]="'organization'" [size]="adminIconSize()" [ariaLabel]="false" />
                            </div>
                        }
                    </div>
                    <div class="org-details">
                        <div class="org-card__header">
                            <h3 [id]="'org-name-' + org().itemId" class="org-name">{{ displayName() }}</h3>
                            @if (userStore.loggedIn()) {
                                <s25-ng-favorite-button
                                    class="float-end"
                                    [itemTypeId]="Item.Ids.Organization"
                                    [itemId]="org().itemId"
                                    [display]="'bookmark'"
                                    (click)="$event.preventDefault(); $event.stopPropagation()"
                                />
                            }
                        </div>
                        @if (!!visibleRoles()?.length) {
                            <div class="org-card__roles">
                                @for (role of visibleRoles(); track role.itemId) {
                                    <span class="org-card__role">{{ role.itemName }}</span>
                                }
                                @if (hiddenRoleCount()) {
                                    <span class="org-card__role org-card__role--more">+{{ hiddenRoleCount() }}</span>
                                }
                            </div>
                        }
                        @if (org()?.mission) {
                            <div
                                class="org-mission"
                                [class.org-mission--clamped-2]="userRoles()?.length && userRoles()!.length <= 2"
                                [class.org-mission--clamped-1]="userRoles()?.length! > 2"
                                [innerHTML]="org().mission! | safeHTML"
                            ></div>
                        }
                        <div class="org-card__actions">
                            <a class="org-link" (click)="navigateToTab($event, 'news')">
                                <s25-ng-icon [type]="'news'" [size]="16" [ariaLabel]="false" />
                                News
                            </a>
                            <a class="org-link" (click)="navigateToTab($event, 'events')">
                                <s25-ng-icon [type]="'calendarCalendar'" [size]="16" [ariaLabel]="false" />
                                Events
                            </a>
                        </div>
                    </div>
                </a>
            }
        </article>
    `,
    styles: `
        :host {
            --color--link-text: var(--color--text);
            --color--link-text-hover: var(--color--text);
            --tile-radius: 12px;
            --tile-radius-sm: 8px;
            --tile-border: 0.5px solid var(--color--border);
            --tile-text: var(--color--text-strong);
            --tile-text-secondary: var(--color--text);
            --tile-text-tertiary: var(--color--text-muted);
            --tile-bg: var(--color--surface-bg);
            --tile-bg-secondary: var(--color--surface-2-bg);
            --tile-info-bg: #e6f1fb;
            --tile-info-fg: #0c447c;
        }

        :host-context(body.nm-party--on) {
            --tile-info-fg: #8dbfe8;
        }

        .org-tile-wrapper {
            background: var(--color--surface-bg);
            border: 1px solid var(--color--border);
            border-radius: 8px;
            display: flex;
            align-items: flex-start;
            height: 100%;
            transition: all 0.3s ease;
        }

        .org-tile-wrapper:hover,
        .org-tile-wrapper:focus-within {
            border-color: var(--color--border-bold-hover, #666);
            box-shadow: 0 4px 12px rgba(148, 163, 184, 0.2);
            transform: translateY(-2px);
        }

        .org-tile-link {
            display: flex;
            flex: 1;
            min-width: 0;
            text-decoration: none;
            overflow: hidden;
        }

        .org-image-container {
            flex-shrink: 0;
            border-radius: 8px;
            border-right: 1px solid var(--color--border);
        }

        .org-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
            border-radius: 8px;
        }

        .org-image-placeholder {
            width: 100%;
            height: 100%;
            background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e0 100%);
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            overflow: hidden;
        }

        .org-details {
            min-width: 0;
            padding: 1em;
            width: 100%;
        }

        .org-card__body {
            min-width: 0;
            grid-column: 3;
        }

        .org-details__header {
            display: flex;
            align-items: center;
            gap: 8px;
            flex-wrap: wrap;
            margin-bottom: 4px;
        }

        .org-name {
            font-size: 16px;
            font-weight: 600;
            color: var(--color--text-strong);
            margin: 0;
        }

        .org-mission {
            font-size: 14px;
            color: var(--color--text);
            margin: 0;
            line-height: 1.45;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .org-card__actions {
            display: flex;
            align-items: center;
            gap: 18px;
            margin-top: auto;
            padding-top: 11px;
            border-top: 1px solid var(--cn-border);
        }

        .org-link {
            color: var(--color-primary);
            display: inline-flex;
            gap: 7px;
            font-size: 13px;
            font-weight: 600;
            transition: color 150ms ease;
        }

        :host-context(body.nm-party--on) .org-link {
            color: #8dbfe8;
        }

        .org-link:hover {
            color: var(--color-primary-darker);
        }

        :host-context(body.nm-party--on) .org-link:hover {
            color: #b7d7f1;
        }

        .org-card__header {
            display: flex;
            align-items: baseline;
            gap: 4px 8px;
            justify-content: space-between;
        }

        .org-card__roles {
            display: flex;
            gap: 0.5em;
            flex-wrap: wrap;
        }

        .org-card__role {
            font-size: 11px;
            color: var(--tile-text-secondary);
            background: var(--tile-bg-secondary);
            padding: 2px 8px;
            border-radius: 4px;
            flex-shrink: 0;
        }

        .org-card__role--more {
            font-style: italic;
            color: var(--tile-text-tertiary);
        }

        :host:not(.is-admin) .org-image-container {
            flex: 0 0 200px;
            aspect-ratio: 1;
        }

        :host:not(.is-admin) .org-details {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 9px;
        }

        :host:not(.is-admin) .org-tile-link {
            min-height: 200px;
            align-self: stretch;
        }

        /* Admin tile styles */
        :host(.is-admin) .org-image-container {
            width: 56px;
            height: 56px;
            border-right: none;
            grid-row: 1 / -1;
        }

        :host(.is-admin) .org-tile-wrapper {
            display: grid;
            grid-template-columns: auto auto minmax(0, 1fr) auto;
            grid-template-rows: auto auto;
            gap: 0 16px;
            padding: 16px;
            align-items: start;
        }

        :host(.is-admin) .org-tile-link {
            display: contents;
            height: auto;
            overflow: visible;
        }

        :host(.is-admin) .org-details {
            grid-column: 3;
            grid-row: 1;
        }

        :host(.is-admin) .org-mission {
            margin-bottom: 10px;
        }

        :host(.is-admin) .org-details {
            padding: 0;
        }

        .btn {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 8px 14px;
            border: var(--tile-border);
            border-radius: var(--tile-radius-sm);
            background: var(--tile-bg);
            font-size: 14px;
            font-weight: 500;
            color: var(--tile-text);
            cursor: pointer;
            transition: background 0.15s;
        }

        .btn:hover {
            background: var(--tile-bg-secondary);
        }

        .btn:focus {
            outline: none;
            box-shadow: var(--shadow--form-focus, 0 0 0 2px #fafaf9, 0 0 0 4px #2573a7);
        }

        .org-tile-link:focus,
        .org-name-link:focus {
            outline: none;
            box-shadow: var(--shadow--form-focus, 0 0 0 2px #fafaf9, 0 0 0 4px #2573a7);
            border-radius: var(--border-radius, 3px);
        }

        .btn--sm {
            padding: 4px 10px;
            font-size: 12px;
        }

        .org-card__check {
            display: block;
            grid-row: 1 / -1;
        }

        :host ::ng-deep s25-ng-checkbox .wrapper {
            margin: 0;
        }

        .org-card__type {
            font-size: 13px;
            color: var(--tile-text-secondary);
            margin: 0 0 10px;
        }

        .org-card__stats {
            display: flex;
            gap: 18px;
            margin-bottom: 10px;
            margin: 0 0 10px 0;
            padding: 0;
        }

        .org-card__stats .stat {
            display: flex;
            flex-direction: column-reverse;
        }

        .org-card__stats .stat dd {
            margin: 0;
        }

        .org-card__contact {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 10px 12px;
            background: var(--tile-bg-secondary);
            border-radius: var(--tile-radius-sm);
            margin-bottom: 8px;
        }

        .contact-info {
            display: flex;
            flex-direction: column;
            gap: 2px;
            min-width: 0;
            flex: 1;
        }

        .contact-label {
            font-size: 11px;
            color: var(--tile-text-tertiary);
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        .contact-name {
            font-size: 13px;
            color: var(--tile-text);
            font-weight: 500;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        :host .org-card__contact ::ng-deep s25-ng-contact-profile-image {
            flex-shrink: 0;
        }

        :host .org-card__contact ::ng-deep .contact-image-placeholder {
            width: 48px !important;
            height: 48px !important;
            border-width: 1px;
            box-shadow: none;
        }

        :host .org-card__contact ::ng-deep .contact-image-placeholder s25-ng-icon {
            transform: scale(0.65);
        }

        :host .org-card__contact ::ng-deep s25-ng-filestore-image,
        :host .org-card__contact ::ng-deep s25-ng-filestore-image img {
            width: 48px !important;
            height: 48px !important;
            min-width: 48px !important;
            min-height: 48px !important;
        }

        .contact-email-btn {
            flex-shrink: 0;
        }

        .org-card__categories {
            margin-bottom: 8px;
        }

        .org-card__rail {
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: 8px;
            grid-row: 1 / -1;
            grid-column: -1;
            min-width: 180px;
        }

        .stat__value {
            font-size: 15px;
            font-weight: 500;
        }

        .stat__label {
            font-size: 11px;
            color: var(--tile-text-tertiary);
        }

        .stat--accent .stat__value {
            color: var(--tile-info-fg);
        }

        .status-wrapper {
            display: inline-flex;
            align-items: center;
            gap: 4px;
        }

        .standing-icon {
            flex-shrink: 0;
        }

        .standing-icon--allow {
            color: #27500a;
        }

        .standing-icon--warn {
            color: #633806;
        }

        .standing-icon--deny {
            color: #791f1f;
        }

        .status-dropdown {
            display: inline-block;
        }

        .org-name-link {
            text-decoration: none;
        }

        :host ::ng-deep .status-dropdown .ngDropdownPaginated {
            border: none !important;
            background: transparent;
            padding: 0;
        }

        :host ::ng-deep .status-dropdown .select2-container {
            min-width: auto;
        }

        :host ::ng-deep .status-dropdown .ngDropdownDrop {
            z-index: 1000;
        }

        :host ::ng-deep .status-dropdown .select2-choice.ui-select-match {
            font-size: 12px;
            font-weight: 500;
            padding: 2px 22px 2px 8px !important;
            border-radius: 999px;
            border: 1px dashed var(--color--border) !important;
            background: var(--color--surface-2-bg) !important;
            color: var(--color--text-muted) !important;
            min-height: auto;
            line-height: 1.4;
        }

        :host ::ng-deep .status-dropdown .select2-choice.ui-select-match::after {
            right: 5px;
            width: 12px;
            height: 12px;
            top: 50%;
            transform: translateY(-50%);
            background-size: 12px 12px;
        }

        :host ::ng-deep .status-dropdown .ngSelectFavorite--wrapper,
        :host ::ng-deep .status-dropdown .ngDropdownPaginated .ngDropdownContent > span {
            padding-right: 0;
        }

        :host ::ng-deep .status-dropdown .select2-container .select2-choice .ngDropdownContent {
            min-height: 0;
        }

        :host ::ng-deep .status-dropdown:not(.has-status) .select2-container .select2-choice .ngDropdownContent {
            color: var(--color--text-muted) !important;
        }

        :host ::ng-deep .status-dropdown:not(.has-status) .select2-chosen,
        :host ::ng-deep .status-dropdown:not(.has-status) .ngDropdownContent span {
            color: var(--color--text-muted) !important;
        }

        :host ::ng-deep .status-dropdown.has-status .select2-choice.ui-select-match {
            border: none;
        }

        :host ::ng-deep .status-dropdown.has-status[data-standing="allow"] .select2-choice.ui-select-match,
        :host ::ng-deep .status-dropdown.has-status[data-standing="allow"] .select2-chosen,
        :host ::ng-deep .status-dropdown.has-status[data-standing="allow"] .ngDropdownContent span,
        :host
            ::ng-deep
            .status-dropdown.has-status[data-standing="allow"]
            .select2-container-active
            .select2-choice.ui-select-match {
            background: #eaf3de !important;
            color: #27500a !important;
        }

        :host ::ng-deep .status-dropdown.has-status[data-standing="warn"] .select2-choice.ui-select-match,
        :host ::ng-deep .status-dropdown.has-status[data-standing="warn"] .select2-chosen,
        :host ::ng-deep .status-dropdown.has-status[data-standing="warn"] .ngDropdownContent span,
        :host
            ::ng-deep
            .status-dropdown.has-status[data-standing="warn"]
            .select2-container-active
            .select2-choice.ui-select-match {
            background: #faeeda !important;
            color: #633806 !important;
        }

        :host ::ng-deep .status-dropdown.has-status[data-standing="deny"] .select2-choice.ui-select-match,
        :host ::ng-deep .status-dropdown.has-status[data-standing="deny"] .select2-chosen,
        :host ::ng-deep .status-dropdown.has-status[data-standing="deny"] .ngDropdownContent span,
        :host
            ::ng-deep
            .status-dropdown.has-status[data-standing="deny"]
            .select2-container-active
            .select2-choice.ui-select-match {
            background: #fcebeb !important;
            color: #791f1f !important;
        }

        .org-card__audit {
            font-size: 11px;
            color: var(--tile-text-tertiary);
            margin: 0;
        }

        @media (max-width: 768px) {
            :host:not(.is-admin) .org-tile-link {
                min-height: auto;
                max-height: 125px;
            }

            :host:not(.is-admin) .org-image-container {
                flex: 0 0 125px;
                aspect-ratio: 1;
                align-self: stretch;
            }

            .org-details {
                padding: 0.75rem;
            }

            .org-name {
                font-size: 14px;
            }

            .org-link {
                font-size: 1rem;
            }

            :host(.is-admin) .org-tile-wrapper {
                display: flex;
                flex-direction: column;
                gap: 12px;
                position: relative;
            }

            :host(.is-admin) .org-image-container {
                width: 80px;
                height: 80px;
            }

            :host(.is-admin) .org-card__body {
                width: 100%;
            }

            :host(.is-admin) .org-card__stats {
                flex-wrap: wrap;
                gap: 12px;
            }

            :host(.is-admin) .org-card__rail {
                flex-direction: row;
                width: 100%;
                justify-content: flex-end;
            }

            :host(.is-admin) .org-card__check {
                position: absolute;
                top: 8px;
                left: 8px;
                z-index: 1;
            }
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        S25IconComponent,
        S25RouterLinkDirective,
        S25DropdownPaginatedModule,
        S25EditablePropertyComponent,
        S25CheckboxModule,
        S25ContactProfileImageComponent,
        S25IconButtonComponent,
        S25PipesModule,
        S25FavoriteButtonComponent,
    ],
})
export class SeOrganizationTileComponent {
    org = input.required<SeOrgTile>();

    private readonly searchStore = inject(SeObjectSearchStore, { optional: true });
    protected readonly userStore = inject(UserStore);
    private readonly itemService = inject(ItemService);

    constructor() {
        effect(() => {
            const { itemId, favorite } = this.org();
            if (typeof favorite !== "boolean") return;
            untracked(() =>
                this.itemService.storeItem({ itemId, itemTypeId: Item.Ids.Organization, favorite }, { favorite: true }),
            );
        });
    }

    readonly isAdmin = computed(() => this.searchStore?.isAdmin());
    readonly adminIconSize = computed(() => (this.isAdmin() ? 40 : 80));

    selected = computed(() => this.searchStore?.selectedIds().has(this.org().itemId) ?? false);

    routingStateName = S25States.names.seOrganizationDetails;
    stateParams = computed(() => ({ itemId: this.org().itemId }));
    protected OrgTypeId = Item.Ids.Organization;

    errorMsg = signal("");

    protected ratingInput = linkedSignal<DropDownItem | undefined>(() => {
        const rating = this.org().rating;
        if (!rating) return undefined;
        return {
            itemId: rating.itemId,
            itemName: rating.itemName,
            organizationStanding: rating.organizationStanding,
        };
    });
    protected ratingStanding = computed(() => this.ratingInput()?.organizationStanding ?? "allow");

    protected standingLabel = computed(() => {
        switch (this.ratingStanding()) {
            case "allow":
                return "Good";
            case "warn":
                return "Warning";
            case "deny":
                return "Restricted";
            default:
                return "";
        }
    });

    protected standingIcon = computed((): "checkCircle" | "warning" | "stop" => {
        switch (this.ratingStanding()) {
            case "allow":
                return "checkCircle";
            case "warn":
                return "warning";
            case "deny":
                return "stop";
            default:
                return "checkCircle";
        }
    });

    protected statusAccessibleLabel = computed(() => {
        const rating = this.ratingInput()?.itemName;
        if (!rating) return "";
        return `Standing: ${this.standingLabel()}. Status: ${rating}.`;
    });

    numMembers = computed(() => S25Array.uniqueByProp(this.org().members ?? [], "itemId")?.length ?? "--");
    numOfficers = computed(
        () =>
            S25Array.uniqueByProp(
                this.org().members?.filter((member) => member.role?.itemId !== S25Const.unassignedRole.organization) ??
                    [],
                "itemId",
            ).length ?? "--",
    );
    modDt = computed(() => S25Datefilter.transform(this.org().lastModified!, this.userStore.dateTimeFormat()!));
    primaryContact = computed(() => this.org().primaryContact);
    private readonly maxVisibleRoles = 3;
    userRoles = computed(() => this.org().userRoles);
    visibleRoles = computed(() => this.userRoles()?.slice(0, this.maxVisibleRoles) ?? []);
    hiddenRoleCount = computed(() => Math.max(0, (this.userRoles()?.length ?? 0) - this.maxVisibleRoles));

    protected logoUrl = linkedSignal(() => this.logoGet.value());

    displayName = computed(() => {
        return this.org()?.title || this.org().itemName;
    });

    private readonly logoGet = s25Resource({
        loader: async () => {
            const existingUrl = transformLogoDisplayUrl(this.org().logoUrl);
            if (existingUrl) {
                return existingUrl;
            }
            if (this.org().logosResolvedFromSearch) {
                return undefined;
            }
            return StudentEngagementService.getLogoUrl(this.OrgTypeId, this.org().itemId).catch((error) => {
                throw error;
            });
        },
    });

    setSelected(isSelected: boolean) {
        if (!this.searchStore) return;
        const id = this.org().itemId;
        if (isSelected) this.searchStore.selectId(id);
        else this.searchStore.deSelectId(id);
    }

    openEmailSidebar() {
        if (!this.searchStore) return;
        const { primaryContact, ...orgWithoutContact } = this.org();
        this.searchStore.openEmailSidebar(orgWithoutContact);
    }

    emailPrimaryContact(event: MouseEvent) {
        event.stopPropagation();
        if (!this.searchStore) return;
        this.searchStore.openEmailSidebar(this.org());
    }

    navigateToTab(event: MouseEvent, tab: string) {
        event.preventDefault();
        event.stopPropagation();
        StateService.go(this.routingStateName, { itemId: this.org().itemId, tab }, null).then(() =>
            window.scrollTo({ top: 0, behavior: "smooth" }),
        );
    }

    async updateRating(rating: DropDownItem | undefined) {
        if (!rating?.itemId) return;
        const [_, error] = await S25Promise.Maybe(
            OrganizationProfileApi.saveRating([this.org().itemId], Number(rating.itemId)),
        );
        if (error) {
            this.errorMsg.set(<string>S25Error.extract(error));
            return;
        }
        this.errorMsg.set("");
        const standing = await OrganizationService.getRatingStanding({
            itemId: Number(rating.itemId),
            itemName: rating.itemName ?? "",
            ratingId: Number(rating.itemId),
        });
        this.ratingInput.set({
            ...rating,
            organizationStanding: standing,
        });
    }

    protected readonly Item = Item;
}
